import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGoBack from "hooks/useGoBack";
import { getPostDetail } from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import { isSameUser } from "utils/userIdentity";
import { IDENTITY_STATUS, usePublicIdentityStatus } from "hooks/usePublicIdBackfill";
import useUserStore from "@store/useUserStore";
import CommunityCreate from "pages/community/CommunityCreate";
import SubHeader from "components/common/SubHeader";
import "pages/community/community.scss";

const CommunityEdit = () => {
    const navigate = useNavigate();
    const goBack = useGoBack();
    const { postId } = useParams();
    const { showError } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);

    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 비로그인 시 로그인 페이지로 리다이렉트
    useEffect(() => {
        if (!userInfo) {
            navigate("/login", { replace: true });
        }
    }, [userInfo, navigate]);

    // 세션은 있는데 public_id 가 아직 없는 구간(backfill 대기)에서는 소유권을 판정할 수 없다.
    // 이때 거부하면 작성자 본인이 튕겨나므로 판정 자체를 보류한다.
    const identityStatus = usePublicIdentityStatus();
    const isOwner =
        identityStatus === IDENTITY_STATUS.RESOLVED && post
            ? isSameUser(userInfo, post)
            : null;

    useEffect(() => {
        const loadPost = async () => {
            try {
                const result = await getPostDetail(postId);
                if (!result.success || !result.data) {
                    showError("게시글을 찾을 수 없습니다.");
                    navigate("/community", { replace: true });
                    return;
                }

                setPost(result.data);
            } catch (error) {
                showError("게시글을 불러오지 못했습니다.");
                navigate("/community", { replace: true });
            } finally {
                setIsLoading(false);
            }
        };
        loadPost();
    }, [postId, navigate, showError]);

    // 소유권 거부는 identity 가 확정된 뒤에만 수행한다.
    useEffect(() => {
        if (isOwner !== false) return;
        showError("수정 권한이 없습니다.");
        navigate(`/community/${postId}`, { replace: true });
    }, [isOwner, postId, navigate, showError]);

    // backfill 이 끝났는데도 public_id 가 없으면 소유권을 영영 판정할 수 없다.
    // 설명 없는 로딩에 가두지 않고 상태와 다음 행동을 알린다.
    if (identityStatus === IDENTITY_STATUS.UNAVAILABLE) {
        return (
            <div className="community-page community-create">
                <SubHeader title="게시글 수정" customGoback={() => goBack()} />
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#757b80", fontSize: 14 }}>
                    본인 확인에 실패했습니다.
                    <br />
                    앱을 새로고침한 뒤 다시 시도해주세요.
                </div>
            </div>
        );
    }

    if (isLoading || isOwner !== true) {
        return (
            <div className="community-page community-create">
                <SubHeader title="게시글 수정" customGoback={() => goBack()} />
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#757b80", fontSize: 14 }}>
                    로딩 중...
                </div>
            </div>
        );
    }

    return (
        <CommunityCreate
            editMode
            postId={postId}
            initialData={post}
        />
    );
};

export default CommunityEdit;
